import React, { useState } from "react";
import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import CustomIcon from "../../Components/CustomIcon";
import { GlobalStyles } from "@mui/material";

const globalStyles = {
  // popupClassName will be applied by AntD to the popup element that lives in body
  ".single-datepicker-popup": {
    zIndex: "1500 !important",
  },
  // optionally also target AntD's default class
  ".ant-picker-dropdown": {
    zIndex: "1500 !important",
  },
};

const SingleDatePicker = ({
  defaultValue = null,
  // Pass `value` to make the field controlled by the parent. `defaultValue` seeds the
  // internal state ONCE, so a parent that sets the date itself (auto-fill from a lookup,
  // reset, prefill from a saved draft) never sees the field update — use `value` there.
  value = undefined,
  handleOnChange,
  minDate = null, // optional: "2025-08-01"
  maxDate = null, // optional: "2025-08-31"
  placeholder = "Select a date",
  format = "DD MMM YYYY",
  status = "", // "" | "error" -> red border
  singleIcon = false, // true: no left prefix, calendar icon on the right only
  fullWidth = false, // true: fill the parent container width
  disabled = false,
  // Optional Cypress handle. Off by default so no existing caller changes.
  dataTestId = undefined,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    defaultValue ? dayjs(defaultValue) : null
  );
  const [isDatePickerHover, setIsDatePickerHover] = React.useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  const isControlled = value !== undefined;
  const currentDate = isControlled
    ? value
      ? dayjs(value)
      : null
    : selectedDate;

  const onChange = (date) => {
    if (!isControlled) setSelectedDate(date);
    handleOnChange(date);
  };

  const onOpenChange = (open) => {
    if (!open) {
      setIsDatePickerHover(false);
    }
    setIsCalendarOpen(open);
  }

  const min = minDate ? dayjs(minDate) : null;
  const max = maxDate ? dayjs(maxDate) : null;

  // Disable dates outside the range
  const disabledDate = (current) => {
    if (!current) return false;
    if (min && current.isBefore(min, "day")) return true;
    if (max && current.isAfter(max, "day")) return true;
    return false;
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#4F44E0",
          // Match the MUI font (Roboto) so date fields render like the other inputs.
          fontFamily: "Roboto",
        },
      }}
    >
      <GlobalStyles styles={globalStyles} />
      <DatePicker
        data-testid={dataTestId}
        popupClassName="single-datepicker-popup"
        value={currentDate}
        onChange={onChange}
        inputReadOnly={true}
        allowClear={false}
        status={status}
        disabled={disabled}
        onMouseEnter={() => setIsDatePickerHover(true)}
        onMouseLeave={() => setIsDatePickerHover(false)}
        onOpenChange={onOpenChange}
        format={format}
        placeholder={placeholder}
        style={
            fullWidth
                ? { width: "100%", height: "42px", borderRadius: "8px", fontSize: "14px" }
                : { fontSize: "14px" }
        }
        disabledDate={disabledDate}
        prefix={
          singleIcon ? undefined : (
            <CustomIcon
              stroke={isCalendarOpen || isDatePickerHover ? "#6D72F6" : "#444B51"}
              name="calendar"
            />
          )
        }
        suffixIcon={
          singleIcon ? (
            <CustomIcon
              stroke={isCalendarOpen || isDatePickerHover ? "#6D72F6" : "#444B51"}
              name="calendar"
            />
          ) : (
            <CustomIcon
              sx={{ fontSize: "11px" }}
              stroke={isCalendarOpen || isDatePickerHover ? "#6D72F6" : "#444B51"}
              name="downArrow"
            />
          )
        }
      />
    </ConfigProvider>
  );
};

export default SingleDatePicker;
